import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, RendererFactory2, Renderer2 } from '@angular/core';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { AuthService } from '@app/services/auth.service';
import { UserService } from '@app/services/user.service';
import { BrowserAuthError, BrowserAuthErrorMessage, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { Environment } from '@environments/interface.environment';
import { forkJoin, fromEvent, Observable, throwError } from 'rxjs';
import { catchError, first, map, tap, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {

  private renderer: Renderer2;

  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService,
    private userService: UserService,
    private rendererFactory: RendererFactory2
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * This should only be called from the main workplace app module to hook into
   * the init process and hold off or continue the bootstrap depending on authentication status.
   * */
  public init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (Utilities.isInIframe(this.window)) {
        resolve();
      } else {
        resolve(this.login(reject));
      }
    });
  }

  private login(reject: (reason?: any) => void) {
    return this.authService.handleRedirect()
      .pipe(
        mergeMap((authenticatedResult) => {
          // The handleredirect will always initialize since we use redirect flow as a fallback.
          // If result is null we're are not authenticated and will need to start a login flow
          // If we have a result we're coming back from an authentication process (using redirect) and we can continue initializing the user and featuresettings.
          if (authenticatedResult) {
            this.authService.useRedirect = true;
            return this.initUserAndFeatures();
          } else {
            if (Utilities.isInIframe(this.window)) {
              // This flow is used by Core Intranet - in this case WP is always iframed inside SharePoint so we dont care about redirect-flow.
              return this.loginPopup()
                .pipe(
                  catchError((err) => {
                    if (err instanceof BrowserAuthError) {
                      // Fallback to create a sign in button if popups are blocked
                      if (err.errorCode === BrowserAuthErrorMessage.pkceNotGenerated.code) {
                        return this.createSignInButtonAndListenOnClick();
                      }
                    }
                    return throwError(err);
                  })
                );
            } else {
              if (this.authService.useRedirect) {
                return this.loginRedirect(reject);
              } else {
                return this.loginPopup()
                  .pipe(
                    catchError((err) => {
                      if (err instanceof BrowserAuthError) {
                        // Fallback to redirect if popup fails due to blocking setting in browser
                        if (err.errorCode === BrowserAuthErrorMessage.pkceNotGenerated.code) {
                          return this.loginRedirect(reject);
                        }
                      }
                      return throwError(err);
                    })
                  );
              }
            }
          }
        })
      ).toPromise();
  }

  private loginPopup(): Observable<void> {
    return this.authService.loginPopup((this.authService.loginRequest as PopupRequest))
      .pipe(
        mergeMap(() => {
          return this.initUserAndFeatures();
        }));
  }

  private loginRedirect(reject: (reason?: any) => void): Observable<void> {
    return this.authService.loginRedirect((this.authService.loginRequest as RedirectRequest))
      .pipe(
        tap(() => {
          reject('Redirect will start now');
        }),
        catchError((err) => {
          if (err instanceof BrowserAuthError) {
            // Create sign-in button if redirect happens in iframe - We will HAVE to use popup flow and the user needs to click the button manually.
            if (err.errorCode === BrowserAuthErrorMessage.redirectInIframeError.code) {
              return this.createSignInButtonAndListenOnClick();
            }
          }
          return throwError(err);
        }));
  }

  private createSignInButtonAndListenOnClick(): Observable<void> {
    const div = this.renderer.createElement('div');
    const button = this.renderer.createElement('button');
    const paragraph = this.renderer.createElement('p');
    const img = this.renderer.createElement('img');

    this.renderer.addClass(div, 'absolute-center-position');

    ['btn', 'btn-primary', 'btn-lg', 'd-block', 'mx-auto'].forEach(cssClass => {
      this.renderer.addClass(button, cssClass);
    });

    ['text-center', 'mt-3'].forEach(cssClass => {
      this.renderer.addClass(paragraph, cssClass);
    });

    ['d-block', 'mx-auto', 'img-thumbnail', 'w-50'].forEach(cssClass => {
      this.renderer.addClass(img, cssClass);
    });

    this.renderer.setAttribute(img, 'src', 'assets/popup_block.png');

    const paragraphText = this.renderer.createText($localize`:@@allow-popups:You can skip this step by allowing popups for this site in your browser (see below).`);
    const buttonText = this.renderer.createText($localize`:@@click-to-sign-in:Click to Sign in`);

    this.renderer.appendChild(paragraph, paragraphText);
    this.renderer.appendChild(button, buttonText);
    this.renderer.appendChild(div, button);
    this.renderer.appendChild(div, paragraph);
    this.renderer.appendChild(div, img);

    this.renderer.appendChild(this.document.body, div);

    return fromEvent(button, 'click')
      .pipe(
        first(), // only listen to the frist click otherwise this observable chain will never complete and the init process will halt.
        mergeMap(() => {
          return this.loginPopup();
        }),
        tap(() => {
          button.remove();
          paragraph.remove();
          img.remove();
          div.remove();
        }));
  }

  private initUserAndFeatures(): Observable<void> {
    return forkJoin([
      this.userService.getOrCreateMe(),
      //this.featureSettingsService.getFeatureSettings()
    ])
      .pipe(map(() => { }));
  }
}
